/**
 * Auto-generated Zod schemas from api-spec.yaml
 *
 * DO NOT EDIT THIS FILE MANUALLY!
 *
 * This file is automatically generated from the OpenAPI specification.
 * To update, run: npm run api:generate-schemas
 *
 * Generated: 2026-09-15T23:17:29.832Z
 * Source: api-spec.yaml v2.17.1
 */

import { z } from 'zod';

/**
 * Typed details are owned by the TypeScript domain contracts until backend schemas are frozen.
 */
export const MvpRecordSchema = z.record(z.string(), z.any());

export type MvpRecord = z.infer<typeof MvpRecordSchema>;

/**
 * 兩層就緒狀態（讀取時計算，永不儲存）。產品就緒 = 建立必填 + 3 種描述
 * （含報關品名）+ Base Price + Costing Method；僅就緒產品可進入 SO/PO。
 * 
 */
export const ProductReadinessSchema = z.object({
  ready: z.boolean(),
  missing: z.array(z.enum(['descriptions', 'pricingRef', 'costingRef']))
});

export type ProductReadiness = z.infer<typeof ProductReadinessSchema>;

/**
 * 客戶料號交叉參照（含休眠的 Customer Item Authorization 欄位 — 本期無 UI）
 */
export const ProductCustomerPartNumberSchema = z.object({
  id: z.string(),
  customerId: z.number(),
  customerName: z.string(),
  partNumber: z.string(),
  description: z.string().optional(),
  isAuthorized: z.boolean().optional(),
  effectiveFrom: z.string().datetime().optional().nullable(),
  effectiveTo: z.string().datetime().optional().nullable()
});

export type ProductCustomerPartNumber = z.infer<typeof ProductCustomerPartNumberSchema>;

/**
 * 備件料號（2026-08-27 §5）— 產品自身的備件別名，對應 Internal Product ID；無客戶／供應商鍵
 */
export const ProductSparePartNumberSchema = z.object({
  id: z.string(),
  partNumber: z.string(),
  description: z.string().optional()
});

export type ProductSparePartNumber = z.infer<typeof ProductSparePartNumberSchema>;

/**
 * 數量比（Figma 1:16 — 1:1、1:6…）；兩側皆為正整數。
 */
export const PairingQtyRatioSchema = z.object({
  parentQty: z.number().min(1),
  componentQty: z.number().min(1)
});

export type PairingQtyRatio = z.infer<typeof PairingQtyRatioSchema>;

/**
 * 產品供應商列（多供應商；有列時恰一 isPrimary）。上次/下次採購價與 MOQ
 * 為 SR1.2（TEGO H51 / US-285）新增 — 前端先行定義，待後端確認。
 * 
 */
export const ProductVendorLineSchema = z.object({
  id: z.string(),
  productId: z.string(),
  vendorId: z.number(),
  vendorName: z.string(),
  vendorSku: z.string().optional(),
  unitCost: z.number(),
  currency: z.string(),
  orderUnit: z.string().optional(),
  isPrimary: z.boolean(),
  isPreferred: z.boolean(),
  leadTimeDays: z.number().optional(),
  lastPurchasePrice: z.number().optional().nullable(),
  nextPurchasePrice: z.number().optional().nullable(),
  moq: z.number().optional().nullable(),
  isActive: z.boolean().optional()
});

export type ProductVendorLine = z.infer<typeof ProductVendorLineSchema>;

/**
 * 車型適配（Make/Model 皆多選 — R26；年份為可重複子表）
 */
export const ProductFitmentSchema = z.object({
  makes: z.array(z.string()),
  models: z.array(z.string()),
  years: z.array(z.object({
  yearFrom: z.number(),
  yearTo: z.number().optional(),
  variant: z.string().optional()
}))
});

export type ProductFitment = z.infer<typeof ProductFitmentSchema>;

/**
 * 產品圖片（最多 5 張、槽位有序 — TEGO K13）
 */
export const ProductImageSlotSchema = z.object({
  slot: z.number().min(1).max(5),
  url: z.string(),
  caption: z.string().optional()
});

export type ProductImageSlot = z.infer<typeof ProductImageSlotSchema>;

/**
 * 16 個會計科目參照＋Tax Schedule，全部為第一天佔位欄位（R217）—
 * GL 邏輯後續接線。Intercompany 科目與 Tax Schedule 為 Blocked-CLARIFY
 * （紫色鎖定、不可編輯、不猜測）。#96 對齊 src/types/api/product.ts
 * ProductAccountingRefs（mock 實測含 customerReturn/vendorReturn/
 * dropship 三欄 — 先前 spec 缺漏會被 Zod parse 剝除）。
 * 
 */
export const ProductAccountingRefsSchema = z.object({
  incomeAccountId: z.string().optional().nullable(),
  cogsAccountId: z.string().optional().nullable(),
  assetAccountId: z.string().optional().nullable(),
  gainLossAccountId: z.string().optional().nullable(),
  priceVarianceAccountId: z.string().optional().nullable(),
  quantityVarianceAccountId: z.string().optional().nullable(),
  exchangeRateVarianceAccountId: z.string().optional().nullable(),
  customerReturnVarianceAccountId: z.string().optional().nullable(),
  vendorReturnVarianceAccountId: z.string().optional().nullable(),
  dropshipExpenseAccountId: z.string().optional().nullable(),
  deferredRevenueAccountId: z.string().optional().nullable(),
  intercompanyIncomeAccountId: z.string().optional().nullable(),
  intercompanyCogsAccountId: z.string().optional().nullable(),
  vendorBillPriceVarianceAccountId: z.string().optional().nullable(),
  vendorBillQuantityVarianceAccountId: z.string().optional().nullable(),
  vendorBillExchangeRateVarianceAccountId: z.string().optional().nullable(),
  taxScheduleId: z.string().optional().nullable()
});

export type ProductAccountingRefs = z.infer<typeof ProductAccountingRefsSchema>;

/**
 * 產品備註（受眾分流 all/procurement/sales/finance — S1.1）。
 * #96 由 src/types/api/product.ts ProductNote 對齊（mock 實測形狀）。
 * 
 */
export const ProductNoteSchema = z.object({
  id: z.string(),
  text: z.string(),
  audience: z.enum(['all', 'procurement', 'sales', 'finance']),
  createdAt: z.string().datetime()
});

export type ProductNote = z.infer<typeof ProductNoteSchema>;

/**
 * PATCH — 與 CreateProductRequest 相同的可變欄位集（全部選填，見該
 * schema）＋isActive＋回溯 SCN 連結；readiness 於每次 PATCH 後重新計算。
 * 系統欄位（internalProductId、readiness、衍生欄位）不可寫。
 * 商務數量／成本清除時送 null（Sales Controls hide-0）；0 仍為合法值。
 * 
 */
export const UpdateProductRequestSchema = z.object({
  isActive: z.boolean().optional(),
  scn: z.string().optional().nullable(),
  minQty: z.number().optional().nullable(),
  minQtyUnit: z.string().optional().nullable(),
  maxQty: z.number().optional().nullable(),
  maxQtyUnit: z.string().optional().nullable(),
  itemDefinedCost: z.number().optional().nullable(),
  itemDefinedCostUnit: z.string().optional().nullable()
});

export type UpdateProductRequest = z.infer<typeof UpdateProductRequestSchema>;

/**
 * SKU 防重檢查結果
 */
export const CheckSkuResponseSchema = z.object({
  available: z.boolean(),
  conflict: z.object({
  id: z.string(),
  internalProductId: z.string(),
  sku: z.string(),
  displayName: z.string(),
  isActive: z.boolean()
}).optional()
});

export type CheckSkuResponse = z.infer<typeof CheckSkuResponseSchema>;

/**
 * 單一產品之 Tier-2 銷售匯總（SR1.3 共用選擇器 selectProductSalesStats
 * 的輸出，訂單毛利報表同源、防分叉）。#96 由 /products/{id}/sales-stats
 * 的內嵌 schema 抽為 component（形狀不變），供產生的 Zod 契約驗證引用。
 * 無歷史時各數值為 0、毛利為 null（顯示「—」，永不 0）。
 * 
 */
export const ProductSalesStatsSchema = z.object({
  productId: z.string(),
  qtySold30: z.number(),
  qtySold90: z.number(),
  qtySold365: z.number(),
  avgDailyDemand: z.number(),
  avgUnitPrice: z.number().nullable(),
  avgMarginPct: z.number().nullable(),
  monthly: z.array(z.object({
  month: z.string(),
  qty: z.number(),
  amount: z.number(),
  margin: z.number().nullable()
})),
  historyDays: z.number(),
  nonZeroMonths: z.number()
});

export type ProductSalesStats = z.infer<typeof ProductSalesStatsSchema>;

/**
 * US-266 PO 建立預填 — 主要供應商＋全部替代列。#96 依 mock handler 實測
 * 形狀補上（先前 200 無 content schema）。無主要供應商時（US-266 demo
 * prod-brake-pad-02）preferredVendorId/preferredVendorName 為 null；
 * alternatives 一律回傳（可為空陣列）。
 * 
 */
export const ProductVendorPrefillSchema = z.object({
  productId: z.string(),
  preferredVendorId: z.number().nullable(),
  preferredVendorName: z.string().nullable(),
  alternatives: z.array(z.object({
  vendorId: z.number(),
  vendorName: z.string(),
  unitCost: z.number(),
  isPrimary: z.boolean(),
  isPreferred: z.boolean()
}))
});

export type ProductVendorPrefill = z.infer<typeof ProductVendorPrefillSchema>;

/**
 * 外部型錄交叉對應（GS1 / Google）；擷取用、無即時同步（Slice 5.1，前端先行）
 */
export const CategoryExternalMappingsSchema = z.object({
  gs1: z.string().optional(),
  google: z.string().optional()
});

export type CategoryExternalMappings = z.infer<typeof CategoryExternalMappingsSchema>;

/**
 * 分類層級設定（可命名雙語標籤；L1-L3 恆啟用，L4/L5 預設隱藏）
 */
export const CategoryLevelConfigSchema = z.object({
  level: z.number().min(1).max(5),
  labelEn: z.string(),
  labelZh: z.string(),
  enabled: z.boolean()
});

export type CategoryLevelConfig = z.infer<typeof CategoryLevelConfigSchema>;

export const CreateProductCategoryRequestSchema = z.object({
  code: z.string(),
  nameEn: z.string(),
  nameZh: z.string(),
  parentId: z.string().nullable(),
  sortOrder: z.number().optional(),
  netsuiteSegmentValueId: z.string().optional(),
  comment: z.string().optional()
});

export type CreateProductCategoryRequest = z.infer<typeof CreateProductCategoryRequestSchema>;

export const ReparentProductCategoryRequestSchema = z.object({
  newParentId: z.string().nullable(),
  comment: z.string()
});

export type ReparentProductCategoryRequest = z.infer<typeof ReparentProductCategoryRequestSchema>;

export const DeactivateProductCategoryRequestSchema = z.object({
  replacementId: z.string().optional(),
  comment: z.string()
});

export type DeactivateProductCategoryRequest = z.infer<typeof DeactivateProductCategoryRequestSchema>;

export const UpdateCategoryLevelRequestSchema = z.object({
  labelEn: z.string().optional(),
  labelZh: z.string().optional(),
  enabled: z.boolean().optional(),
  comment: z.string()
});

export type UpdateCategoryLevelRequest = z.infer<typeof UpdateCategoryLevelRequestSchema>;

/**
 * 分類異動稽核列（W10 稽核檢視資料來源）
 */
export const CategoryAuditRowSchema = z.object({
  id: z.string(),
  at: z.string().datetime(),
  action: z.enum(['create', 'rename', 'reparent', 'deactivate', 'level_update']),
  targetId: z.string(),
  comment: z.string(),
  before: z.record(z.string(), z.any()).optional(),
  after: z.record(z.string(), z.any()).optional()
});

export type CategoryAuditRow = z.infer<typeof CategoryAuditRowSchema>;

export const ErrorSchema = z.object({
  error: z.string().optional()
});

export type Error = z.infer<typeof ErrorSchema>;

export const PaginationSchema = z.object({
  page: z.number().optional(),
  pageSize: z.number().optional(),
  total: z.number().optional(),
  totalPages: z.number().optional()
});

export type Pagination = z.infer<typeof PaginationSchema>;

/**
 * 聯絡人的一筆所屬公司 (#92 聯絡人身份)：同一位聯絡人可同時連結多家供應商／客戶
 */
export const ContactAffiliationSchema = z.object({
  companyType: z.enum(['vendor', 'customer']),
  companyId: z.number(),
  companyName: z.string(),
  roleTitle: z.string().optional().nullable(),
  isPrimary: z.boolean().optional()
});

export type ContactAffiliation = z.infer<typeof ContactAffiliationSchema>;

/**
 * 建立／更新聯絡人時的所屬公司輸入 (#92)；companyName 由後端依 companyId 解析
 */
export const ContactAffiliationInputSchema = z.object({
  companyType: z.enum(['vendor', 'customer']),
  companyId: z.number(),
  companyName: z.string().optional(),
  roleTitle: z.string().optional().nullable(),
  isPrimary: z.boolean().optional()
});

export type ContactAffiliationInput = z.infer<typeof ContactAffiliationInputSchema>;

export const UserProfileSchema = z.object({
  userId: z.number().optional(),
  username: z.string().optional(),
  fullName: z.string().optional(),
  email: z.string().email().optional(),
  role: z.string().optional(),
  permissions: z.array(z.string()).optional(),
  department: z.string().optional(),
  isActive: z.boolean().optional()
});

export type UserProfile = z.infer<typeof UserProfileSchema>;

export const CategoryTypeSchema = z.object({
  typeId: z.number().optional(),
  type: z.string().optional()
});

export type CategoryType = z.infer<typeof CategoryTypeSchema>;

export const CategoryInfoSchema = z.object({
  categoryId: z.number(),
  category: z.string(),
  color: z.string().optional()
});

export type CategoryInfo = z.infer<typeof CategoryInfoSchema>;

export const CategoryCreateSchema = z.object({
  category: z.string().max(255),
  typeIds: z.array(z.number()),
  description: z.string().max(500).optional(),
  color: z.string().max(7).optional(),
  isActive: z.boolean().optional()
});

export type CategoryCreate = z.infer<typeof CategoryCreateSchema>;

export const CategoryUpdateSchema = z.object({
  category: z.string().max(255),
  typeIds: z.array(z.number()),
  description: z.string().max(500).optional(),
  color: z.string().max(7).optional(),
  isActive: z.boolean().optional()
});

export type CategoryUpdate = z.infer<typeof CategoryUpdateSchema>;

export const CompanySchema = z.object({
  id: z.string().optional(),
  companyName: z.string().optional(),
  taxId: z.string().optional(),
  address: z.string().optional(),
  phoneNumber: z.string().optional(),
  vendorId: z.number().optional(),
  customerId: z.number().optional()
});

export type Company = z.infer<typeof CompanySchema>;

export const InternalContactSchema = z.object({
  userId: z.number(),
  username: z.string(),
  fullName: z.string(),
  email: z.string().email(),
  createdAt: z.string().datetime().optional(),
  updatedAt: z.string().datetime().optional()
});

export type InternalContact = z.infer<typeof InternalContactSchema>;

export const VendorCreateSchema = z.object({
  name: z.string().max(255),
  alternativeName: z.string().max(255).optional(),
  nickname: z.string().max(255).optional(),
  individual: z.boolean(),
  vendorCode: z.string().max(50),
  localTaxId: z.string().max(50),
  categoryIds: z.array(z.number()),
  primaryPort: z.string().max(255).optional(),
  internalContactUserId: z.number().optional(),
  email: z.string().email().optional(),
  fax: z.string().max(50).optional(),
  phoneNumber: z.string().max(50).optional(),
  web: z.string().url().optional(),
  notes: z.string().optional(),
  countryId: z.number(),
  stateId: z.number().optional(),
  cityId: z.number().optional(),
  postcode: z.string().max(20),
  address: z.string(),
  isActive: z.boolean().optional()
});

export type VendorCreate = z.infer<typeof VendorCreateSchema>;

export const VendorUpdateSchema = z.object({
  name: z.string().max(255),
  alternativeName: z.string().max(255).optional(),
  nickname: z.string().max(255).optional(),
  individual: z.boolean(),
  vendorCode: z.string().max(50),
  localTaxId: z.string().max(50).optional(),
  categoryIds: z.array(z.number()),
  primaryPort: z.string().max(255).optional(),
  email: z.string().email().optional(),
  fax: z.string().max(50).optional(),
  phoneNumber: z.string().max(50).optional(),
  web: z.string().url().optional(),
  notes: z.string().optional(),
  countryId: z.number(),
  stateId: z.number().optional(),
  cityId: z.number().optional(),
  postcode: z.string().max(20),
  address: z.string(),
  addressLang: z.string().max(3).optional(),
  isActive: z.boolean().optional()
});

export type VendorUpdate = z.infer<typeof VendorUpdateSchema>;

/**
 * PATCH 方法僅更新請求中提供的欄位，未提供的欄位保持不變
 */
export const VendorPatchSchema = z.object({
  name: z.string().max(255).optional(),
  alternativeName: z.string().max(255).optional(),
  individual: z.boolean().optional(),
  nickname: z.string().max(255).optional(),
  vendorCode: z.string().max(50).optional(),
  localTaxId: z.string().max(50).optional(),
  categoryIds: z.array(z.number()).optional(),
  primaryPort: z.string().max(255).optional(),
  email: z.string().email().optional(),
  fax: z.string().max(50).optional(),
  phoneNumber: z.string().max(50).optional(),
  web: z.string().url().optional(),
  notes: z.string().optional(),
  countryId: z.number().optional(),
  stateId: z.number().optional(),
  cityId: z.number().optional(),
  postcode: z.string().max(20).optional(),
  address: z.string().optional(),
  addressLang: z.string().max(3).optional(),
  isActive: z.boolean().optional()
});

export type VendorPatch = z.infer<typeof VendorPatchSchema>;

export const PaymentTermSchema = z.object({
  paymentTermId: z.number(),
  termCode: z.string(),
  termNameEn: z.string(),
  termNameZh: z.string(),
  dueDateBase: z.enum(['invoice_date', 'delivery_date', 'eom', 'receipt_date']),
  dueDays: z.number(),
  hasDiscount: z.boolean(),
  discountRate: z.number(),
  discountDays: z.number(),
  description: z.string()
});

export type PaymentTerm = z.infer<typeof PaymentTermSchema>;

export const TaxTypeSchema = z.object({
  taxTypeId: z.number(),
  taxType: z.string(),
  taxRate: z.number(),
  description: z.string().optional()
});

export type TaxType = z.infer<typeof TaxTypeSchema>;

export const VendorPaymentSchema = z.object({
  paymentId: z.number().optional(),
  vendorCustomTermId: z.number().optional(),
  paymentTerm: z.object({
  basePaymentTermId: z.number().optional(),
  termCode: z.string().optional(),
  termName: z.string().optional(),
  dueDateBase: z.string().optional(),
  dueDays: z.number().optional(),
  hasDiscount: z.boolean().optional(),
  discountRate: z.number().optional(),
  discountDays: z.number().optional(),
  description: z.string().optional()
}).optional(),
  primaryCurrency: z.string().optional(),
  taxTypeId: z.number().optional(),
  taxType: z.string().optional(),
  taxRate: z.number().optional(),
  createdAt: z.string().datetime().optional(),
  updatedAt: z.string().datetime().optional()
});

export type VendorPayment = z.infer<typeof VendorPaymentSchema>;

export const VendorPaymentCreateSchema = z.object({
  vendorId: z.number(),
  basePaymentTermId: z.number(),
  customTermName: z.string().max(100).optional(),
  customDueDateBase: z.string().max(20).optional(),
  customDueDays: z.number().optional(),
  customHasDiscount: z.boolean().optional(),
  customDiscountRate: z.number().optional(),
  customDiscountDays: z.number().optional(),
  customDescription: z.string().optional(),
  primaryCurrency: z.string().max(3),
  taxTypeId: z.number()
});

export type VendorPaymentCreate = z.infer<typeof VendorPaymentCreateSchema>;

export const VendorPaymentUpdateSchema = z.object({
  basePaymentTermId: z.number(),
  customTermName: z.string().max(100).optional(),
  customDueDateBase: z.string().max(20).optional(),
  customDueDays: z.number().optional(),
  customHasDiscount: z.boolean().optional(),
  customDiscountRate: z.number().optional(),
  customDiscountDays: z.number().optional(),
  customDescription: z.string().optional(),
  primaryCurrency: z.string().max(3),
  taxTypeId: z.number()
});

export type VendorPaymentUpdate = z.infer<typeof VendorPaymentUpdateSchema>;

export const VendorBankSchema = z.object({
  bankId: z.number().optional(),
  isDefault: z.boolean().optional(),
  accountName: z.string().optional(),
  countryId: z.number().optional(),
  countryNameEn: z.string().optional(),
  countryNameZh: z.string().optional(),
  bankName: z.string().optional(),
  bankCode: z.string().optional(),
  branchName: z.string().optional(),
  branchCode: z.string().optional(),
  currency: z.string().optional(),
  accountNumber: z.string().optional(),
  swiftCode: z.string().optional(),
  iban: z.string().optional(),
  notes: z.string().optional(),
  isActive: z.boolean().optional(),
  createdAt: z.string().datetime().optional(),
  updatedAt: z.string().datetime().optional()
});

export type VendorBank = z.infer<typeof VendorBankSchema>;

export const VendorBankCreateSchema = z.object({
  vendorId: z.number(),
  isDefault: z.boolean().optional(),
  accountName: z.string().max(255),
  countryId: z.number(),
  bankName: z.string().max(255),
  bankCode: z.string().max(50),
  branchName: z.string().max(255).optional(),
  branchCode: z.string().max(50).optional(),
  currency: z.string().max(3),
  accountNumber: z.string().max(100),
  swiftCode: z.string().max(50).optional(),
  iban: z.string().max(50).optional(),
  notes: z.string().optional(),
  isActive: z.boolean().optional()
});

export type VendorBankCreate = z.infer<typeof VendorBankCreateSchema>;

export const VendorBankUpdateSchema = z.object({
  isDefault: z.boolean().optional(),
  accountName: z.string().max(255),
  countryId: z.number(),
  bankName: z.string().max(255),
  bankCode: z.string().max(50),
  branchName: z.string().max(255).optional(),
  branchCode: z.string().max(50).optional(),
  currency: z.string().max(3),
  accountNumber: z.string().max(100),
  swiftCode: z.string().max(50).optional(),
  iban: z.string().max(50).optional(),
  notes: z.string().optional(),
  isActive: z.boolean().optional()
});

export type VendorBankUpdate = z.infer<typeof VendorBankUpdateSchema>;

export const BankInfoSchema = z.object({
  bankId: z.number().optional(),
  bankCode: z.string().optional(),
  name: z.string().optional()
});

export type BankInfo = z.infer<typeof BankInfoSchema>;

export const BankBranchInfoSchema = z.object({
  branchId: z.number().optional(),
  branchCode: z.string().optional(),
  name: z.string().optional()
});

export type BankBranchInfo = z.infer<typeof BankBranchInfoSchema>;

export const VendorTransactionSchema = z.object({
  transactionId: z.number().optional(),
  date: z.string().optional(),
  invoiceNumber: z.string().optional(),
  purchaseOrder: z.string().optional(),
  currency: z.string().optional(),
  amountForeignCurrency: z.number().optional(),
  amount: z.number().optional(),
  transactionType: z.enum(['Invoice', 'Payment', 'Credit Note', 'Debit Note']).optional(),
  status: z.enum(['Pending', 'Paid', 'Overdue', 'Cancelled']).optional(),
  notes: z.string().optional(),
  createdAt: z.string().datetime().optional(),
  updatedAt: z.string().datetime().optional()
});

export type VendorTransaction = z.infer<typeof VendorTransactionSchema>;

export const VendorLocationSchema = z.object({
  locationId: z.number().optional(),
  vendorId: z.number().optional(),
  locationName: z.string().optional(),
  isDefaultReceiving: z.boolean().optional(),
  countryId: z.number().optional(),
  country: z.string().optional(),
  stateId: z.number().optional(),
  state: z.string().optional(),
  cityId: z.number().optional(),
  city: z.string().optional(),
  postcode: z.string().optional(),
  address: z.string().optional(),
  addressLang: z.string().optional(),
  parseStatus: z.enum(['parsed', 'pending', 'failed']).optional(),
  notes: z.string().optional(),
  isActive: z.boolean().optional(),
  createdAt: z.string().datetime().optional(),
  updatedAt: z.string().datetime().optional()
});

export type VendorLocation = z.infer<typeof VendorLocationSchema>;

export const VendorLocationCreateSchema = z.object({
  vendorId: z.number(),
  locationName: z.string().max(255),
  isDefaultReceiving: z.boolean().optional(),
  countryId: z.number(),
  stateId: z.number().optional(),
  cityId: z.number().optional(),
  postcode: z.string().max(20),
  address: z.string(),
  addressLang: z.string().max(3).optional(),
  notes: z.string().optional(),
  isActive: z.boolean().optional()
});

export type VendorLocationCreate = z.infer<typeof VendorLocationCreateSchema>;

export const VendorLocationUpdateSchema = z.object({
  locationName: z.string().max(255),
  isDefaultReceiving: z.boolean().optional(),
  countryId: z.number(),
  stateId: z.number().optional(),
  cityId: z.number().optional(),
  postcode: z.string().max(20),
  address: z.string(),
  addressLang: z.string().max(3).optional(),
  notes: z.string().optional(),
  isActive: z.boolean().optional()
});

export type VendorLocationUpdate = z.infer<typeof VendorLocationUpdateSchema>;

export const AuditLogSchema = z.object({
  logId: z.number().optional(),
  operationType: z.enum(['CREATE', 'UPDATE', 'DELETE']).optional(),
  changedFields: z.array(z.string()).optional(),
  oldValues: z.record(z.string(), z.any()).optional(),
  newValues: z.record(z.string(), z.any()).optional(),
  user: z.object({
  userId: z.number().optional(),
  username: z.string().optional(),
  fullName: z.string().optional()
}).optional(),
  ipAddress: z.string().optional(),
  createdAt: z.string().datetime().optional()
});

export type AuditLog = z.infer<typeof AuditLogSchema>;

export const ContinentSchema = z.object({
  continentId: z.number().optional(),
  continentCode: z.string().optional(),
  continentName: z.string().optional()
});

export type Continent = z.infer<typeof ContinentSchema>;

export const CountrySchema = z.object({
  countryId: z.number().optional(),
  countryCode: z.string().optional(),
  countryName: z.string().optional(),
  phoneCode: z.string().optional()
});

export type Country = z.infer<typeof CountrySchema>;

export const StateSchema = z.object({
  stateId: z.number().optional(),
  stateCode: z.string().optional(),
  stateName: z.string().optional()
});

export type State = z.infer<typeof StateSchema>;

export const CitySchema = z.object({
  cityId: z.number().optional(),
  cityName: z.string().optional(),
  postcode: z.string().optional()
});

export type City = z.infer<typeof CitySchema>;

export const LocationByPostcodeSchema = z.object({
  countryId: z.number().optional(),
  countryName: z.string().optional(),
  stateId: z.number().optional(),
  stateName: z.string().optional(),
  cityId: z.number().optional(),
  cityName: z.string().optional(),
  postcode: z.string().optional()
});

export type LocationByPostcode = z.infer<typeof LocationByPostcodeSchema>;

/**
 * 公司資訊（用於檢查公司是否存在）
 */
export const CompanyInfoSchema = z.object({
  companyId: z.number().optional(),
  companyName: z.string().optional(),
  alternativeName: z.string().optional().nullable(),
  nickname: z.string().optional().nullable(),
  localTaxId: z.string().optional(),
  isIndividual: z.boolean().optional(),
  email: z.string().email().optional().nullable(),
  fax: z.string().optional().nullable(),
  phoneCountryCode: z.string().optional().nullable(),
  phoneNumber: z.string().optional().nullable(),
  phoneExtension: z.string().optional().nullable(),
  web: z.string().url().optional().nullable(),
  countryId: z.number().optional().nullable(),
  stateId: z.number().optional().nullable(),
  cityId: z.number().optional().nullable(),
  postcode: z.string().optional().nullable(),
  address: z.string().optional().nullable(),
  addressLang: z.string().optional().nullable()
});

export type CompanyInfo = z.infer<typeof CompanyInfoSchema>;

/**
 * 建立客戶所需資料
 */
export const CustomerCreateSchema = z.object({
  name: z.string().max(255),
  alternativeName: z.string().max(255).optional(),
  nickname: z.string().max(255).optional(),
  individual: z.boolean(),
  customerCode: z.string().max(50),
  localTaxId: z.string().max(50),
  categoryIds: z.array(z.number()),
  internalContactUserId: z.number().optional(),
  parentCompanyId: z.number().optional(),
  defaultLocationId: z.number().optional(),
  isActiveOnlineAccount: z.boolean().optional(),
  email: z.string().email().optional(),
  fax: z.string().max(50).optional(),
  phoneCountryCode: z.string().max(10).optional(),
  phoneNumber: z.string().max(50).optional(),
  phoneExtension: z.string().max(20).optional(),
  web: z.string().url().optional(),
  accountNotes: z.string().optional(),
  deliveryNotes: z.string().optional(),
  salesNotes: z.string().optional(),
  countryId: z.number(),
  stateId: z.number().optional(),
  cityId: z.number().optional(),
  postcode: z.string().max(20),
  address: z.string(),
  addressLang: z.string().max(3).optional(),
  isActive: z.boolean().optional()
});

export type CustomerCreate = z.infer<typeof CustomerCreateSchema>;

/**
 * 更新客戶所需資料
 */
export const CustomerUpdateSchema = z.object({
  name: z.string().max(255),
  alternativeName: z.string().max(255).optional(),
  nickname: z.string().max(255).optional(),
  individual: z.boolean(),
  customerCode: z.string().max(50),
  localTaxId: z.string().max(50).optional(),
  categoryIds: z.array(z.number()),
  internalContactUserId: z.number().optional(),
  parentCompanyId: z.number().optional(),
  defaultLocationId: z.number().optional(),
  isActiveOnlineAccount: z.boolean().optional(),
  email: z.string().email().optional(),
  fax: z.string().max(50).optional(),
  phoneCountryCode: z.string().max(10).optional(),
  phoneNumber: z.string().max(50).optional(),
  phoneExtension: z.string().max(20).optional(),
  web: z.string().url().optional(),
  accountNotes: z.string().optional(),
  deliveryNotes: z.string().optional(),
  salesNotes: z.string().optional(),
  countryId: z.number(),
  stateId: z.number().optional(),
  cityId: z.number().optional(),
  postcode: z.string().max(20),
  address: z.string(),
  addressLang: z.string().max(3).optional(),
  isActive: z.boolean().optional()
});

export type CustomerUpdate = z.infer<typeof CustomerUpdateSchema>;

/**
 * 客戶銀行帳戶資訊
 */
export const CustomerBankSchema = z.object({
  bankId: z.number().optional(),
  customerId: z.number().optional(),
  isDefault: z.boolean().optional(),
  accountName: z.string().optional(),
  countryId: z.number().optional(),
  countryNameEn: z.string().optional(),
  countryNameZh: z.string().optional(),
  bankName: z.string().optional(),
  bankCode: z.string().optional(),
  branchName: z.string().optional().nullable(),
  branchCode: z.string().optional().nullable(),
  currency: z.string().optional(),
  accountNumber: z.string().optional(),
  swiftCode: z.string().optional().nullable(),
  iban: z.string().optional().nullable(),
  notes: z.string().optional().nullable(),
  isActive: z.boolean().optional(),
  createdAt: z.string().datetime().optional(),
  updatedAt: z.string().datetime().optional()
});

export type CustomerBank = z.infer<typeof CustomerBankSchema>;

/**
 * 建立客戶銀行帳戶
 */
export const CustomerBankCreateSchema = z.object({
  customerId: z.number(),
  isDefault: z.boolean().optional(),
  accountName: z.string().max(255),
  countryId: z.number(),
  bankName: z.string().max(255),
  bankCode: z.string().max(50),
  branchName: z.string().max(255).optional(),
  branchCode: z.string().max(50).optional(),
  currency: z.string().max(3),
  accountNumber: z.string().max(100),
  swiftCode: z.string().max(50).optional(),
  iban: z.string().max(50).optional(),
  notes: z.string().optional(),
  isActive: z.boolean().optional()
});

export type CustomerBankCreate = z.infer<typeof CustomerBankCreateSchema>;

/**
 * 更新客戶銀行帳戶
 */
export const CustomerBankUpdateSchema = z.object({
  isDefault: z.boolean().optional(),
  accountName: z.string().max(255),
  countryId: z.number(),
  bankName: z.string().max(255),
  bankCode: z.string().max(50),
  branchName: z.string().max(255).optional(),
  branchCode: z.string().max(50).optional(),
  currency: z.string().max(3),
  accountNumber: z.string().max(100),
  swiftCode: z.string().max(50).optional(),
  iban: z.string().max(50).optional(),
  notes: z.string().optional(),
  isActive: z.boolean().optional()
});

export type CustomerBankUpdate = z.infer<typeof CustomerBankUpdateSchema>;

export const CustomerLocationSchema = z.object({
  locationId: z.number().optional(),
  customerId: z.number().optional(),
  locationName: z.string().optional(),
  isDefaultShipping: z.boolean().optional(),
  countryId: z.number().optional(),
  country: z.string().optional(),
  stateId: z.number().optional(),
  state: z.string().optional(),
  cityId: z.number().optional(),
  city: z.string().optional(),
  postcode: z.string().optional(),
  address: z.string().optional(),
  addressLang: z.string().optional(),
  parseStatus: z.enum(['parsed', 'pending', 'failed']).optional(),
  notes: z.string().optional(),
  isActive: z.boolean().optional(),
  createdAt: z.string().datetime().optional(),
  updatedAt: z.string().datetime().optional()
});

export type CustomerLocation = z.infer<typeof CustomerLocationSchema>;

export const CustomerLocationCreateSchema = z.object({
  customerId: z.number(),
  locationName: z.string().max(255),
  isDefaultShipping: z.boolean().optional(),
  countryId: z.number(),
  stateId: z.number().optional(),
  cityId: z.number().optional(),
  postcode: z.string().max(20),
  address: z.string(),
  addressLang: z.string().max(3).optional(),
  notes: z.string().optional(),
  isActive: z.boolean().optional()
});

export type CustomerLocationCreate = z.infer<typeof CustomerLocationCreateSchema>;

export const CustomerLocationUpdateSchema = z.object({
  locationName: z.string().max(255),
  isDefaultShipping: z.boolean().optional(),
  countryId: z.number(),
  stateId: z.number().optional(),
  cityId: z.number().optional(),
  postcode: z.string().max(20),
  address: z.string(),
  addressLang: z.string().max(3).optional(),
  notes: z.string().optional(),
  isActive: z.boolean().optional()
});

export type CustomerLocationUpdate = z.infer<typeof CustomerLocationUpdateSchema>;

export const CustomerPaymentSchema = z.object({
  paymentId: z.number().optional(),
  customerId: z.number().optional(),
  priceLevelId: z.number().optional().nullable(),
  defaultReceivablesAccount: z.string().optional().nullable(),
  depositPercentage: z.number().min(0).max(100).optional().nullable(),
  cashDiscountPercentage: z.number().min(0).max(100).optional().nullable(),
  agreedTerms: z.object({
  termId: z.number().optional(),
  termCode: z.string().optional(),
  termName: z.string().optional()
}).optional().nullable(),
  dueInDays: z.number().optional().nullable(),
  creditLimit: z.number().optional().nullable(),
  holdStatus: z.enum(['none', 'credit_hold', 'order_hold', 'ship_hold', 'all_hold']).optional().nullable(),
  primaryCurrency: z.string().max(3).optional(),
  taxType: z.object({
  taxTypeId: z.number().optional(),
  taxCode: z.string().optional(),
  taxName: z.string().optional(),
  taxRate: z.number().optional()
}).optional().nullable(),
  invoiceeContactId: z.number().optional().nullable(),
  invoiceeContactName: z.string().optional().nullable(),
  shippingInstruction: z.string().optional().nullable(),
  defaultShippingMarkFront: z.string().optional().nullable(),
  defaultShippingMarkSide: z.string().optional().nullable(),
  createdAt: z.string().datetime().optional(),
  updatedAt: z.string().datetime().optional()
});

export type CustomerPayment = z.infer<typeof CustomerPaymentSchema>;

export const CustomerPaymentCreateSchema = z.object({
  priceLevelId: z.number().optional().nullable(),
  defaultReceivablesAccount: z.string().max(100).optional().nullable(),
  depositPercentage: z.number().min(0).max(100).optional().nullable(),
  cashDiscountPercentage: z.number().min(0).max(100).optional().nullable(),
  basePaymentTermId: z.number().optional().nullable(),
  customTermName: z.string().max(100).optional().nullable(),
  customDueDays: z.number().optional().nullable(),
  customHasDiscount: z.boolean().optional().nullable(),
  customDiscountRate: z.number().optional().nullable(),
  customDiscountDays: z.number().optional().nullable(),
  dueInDays: z.number().optional().nullable(),
  creditLimit: z.number().optional().nullable(),
  holdStatus: z.enum(['none', 'credit_hold', 'order_hold', 'ship_hold', 'all_hold']).optional().nullable(),
  primaryCurrency: z.string().max(3),
  taxTypeId: z.number(),
  invoiceeContactId: z.number().optional().nullable(),
  shippingInstruction: z.string().optional().nullable(),
  defaultShippingMarkFront: z.string().optional().nullable(),
  defaultShippingMarkSide: z.string().optional().nullable()
});

export type CustomerPaymentCreate = z.infer<typeof CustomerPaymentCreateSchema>;

export const CustomerPaymentUpdateSchema = z.object({
  priceLevelId: z.number().optional().nullable(),
  defaultReceivablesAccount: z.string().max(100).optional().nullable(),
  depositPercentage: z.number().min(0).max(100).optional().nullable(),
  cashDiscountPercentage: z.number().min(0).max(100).optional().nullable(),
  basePaymentTermId: z.number().optional().nullable(),
  customTermName: z.string().max(100).optional().nullable(),
  customDueDays: z.number().optional().nullable(),
  customHasDiscount: z.boolean().optional().nullable(),
  customDiscountRate: z.number().optional().nullable(),
  customDiscountDays: z.number().optional().nullable(),
  dueInDays: z.number().optional().nullable(),
  creditLimit: z.number().optional().nullable(),
  holdStatus: z.enum(['none', 'credit_hold', 'order_hold', 'ship_hold', 'all_hold']).optional().nullable(),
  primaryCurrency: z.string().max(3),
  taxTypeId: z.number(),
  invoiceeContactId: z.number().optional().nullable(),
  shippingInstruction: z.string().optional().nullable(),
  defaultShippingMarkFront: z.string().optional().nullable(),
  defaultShippingMarkSide: z.string().optional().nullable()
});

export type CustomerPaymentUpdate = z.infer<typeof CustomerPaymentUpdateSchema>;

/**
 * 定價層嘗試紀錄 — 每層恰出現一次；被高優先層壓過者保留其候選價（anti-175% 軌跡）
 */
export const PriceTraceEntrySchema = z.object({
  tier: z.enum(['special_override', 'customer_level', 'list_price']),
  matched: z.boolean(),
  won: z.boolean(),
  candidatePrice: z.number().optional(),
  ruleRef: z.string().optional(),
  skipReason: z.enum(['no_customer', 'no_rule_found', 'outside_validity', 'inactive', 'no_price_level_assigned', 'price_level_inactive', 'no_band_match', 'no_markup_rule', 'no_cost_record', 'lower_precedence']).optional()
});

export type PriceTraceEntry = z.infer<typeof PriceTraceEntrySchema>;

/**
 * 價格構成瀑布列（成本→Landed→Pricing Base→List → 層別/特價）
 */
export const PriceWaterfallRowSchema = z.object({
  key: z.string(),
  labelKey: z.string(),
  formula: z.string().optional().nullable(),
  amount: z.number()
});

export type PriceWaterfallRow = z.infer<typeof PriceWaterfallRowSchema>;

export const ConfirmChecklistItemSchema = z.object({
  key: z.enum(['customer_active', 'products_ready', 'price_resolvable', 'credit', 'required_fields']),
  ok: z.boolean(),
  blockers: z.array(z.string())
});

export type ConfirmChecklistItem = z.infer<typeof ConfirmChecklistItemSchema>;

export const CreateSalesOrderLineInputSchema = z.object({
  productId: z.string(),
  qty: z.number().min(1),
  unitPrice: z.number().optional(),
  overrideReason: z.string().optional(),
  customerPartNumber: z.string().optional(),
  discountPct: z.number().optional()
});

export type CreateSalesOrderLineInput = z.infer<typeof CreateSalesOrderLineInputSchema>;

export const SalesOrderImportIssueSchema = z.object({
  row: z.number(),
  externalOrderNumber: z.string().nullable(),
  code: z.enum(['UNMATCHED_PART_NO', 'MISSING_CUSTOMER', 'MISSING_EXTERNAL_ORDER_NUMBER', 'INVALID_QUANTITY', 'INVALID_PRICE', 'PRODUCT_NOT_READY', 'PRODUCT_INACTIVE', 'SO_LOCKED']),
  message: z.string(),
  partNumber: z.string().optional()
});

export type SalesOrderImportIssue = z.infer<typeof SalesOrderImportIssueSchema>;

/**
 * 訂單異常佇列項 — 每個 UNMATCHED_PART_NO 一筆；S2.4 提供對照與重處理
 */
export const SalesOrderImportExceptionSchema = z.object({
  id: z.string(),
  externalOrderNumber: z.string(),
  customerId: z.number().nullable(),
  partNumber: z.string(),
  qty: z.number(),
  unitPrice: z.number().nullable(),
  row: z.number(),
  status: z.enum(['open', 'mapped', 'dismissed']),
  createdAt: z.string().datetime()
});

export type SalesOrderImportException = z.infer<typeof SalesOrderImportExceptionSchema>;

/**
 * SO↔PO 第一級 join（S3.1）— 非任一單據上的 FK。兩份單據共同讀取；
 * 進貨經由此列自動回沖 SO 數量分段（S3.3 投影 poLineIds）。
 * direct = 1 SO 行:1 PO 行；aggregate = 多 SO 行併 1 PO 行。
 * 
 */
export const AllocationSchema = z.object({
  id: z.string(),
  soId: z.string(),
  soLineId: z.string(),
  poId: z.string(),
  poLineId: z.string(),
  qty: z.number(),
  mode: z.enum(['direct', 'aggregate']),
  createdAt: z.string().datetime(),
  soNumber: z.string(),
  poNumber: z.string(),
  productId: z.string()
});

export type Allocation = z.infer<typeof AllocationSchema>;

export const PoSecondCheckPriceRowSchema = z.object({
  poNumber: z.string(),
  orderDate: z.string(),
  vendorId: z.number(),
  vendorName: z.string(),
  purchasePrice: z.number(),
  currency: z.string()
});

export type PoSecondCheckPriceRow = z.infer<typeof PoSecondCheckPriceRowSchema>;

export const CreatePurchaseOrderAllocationInputSchema = z.object({
  soLineId: z.string(),
  qty: z.number().min(1)
});

export type CreatePurchaseOrderAllocationInput = z.infer<typeof CreatePurchaseOrderAllocationInputSchema>;

/**
 * ReasonRequiredModal 契約 — actor 與 reason 皆必填
 */
export const PurchaseOrderDecisionRequestSchema = z.object({
  decision: z.enum(['approve', 'reject']),
  actor: z.string(),
  reason: z.string()
});

export type PurchaseOrderDecisionRequest = z.infer<typeof PurchaseOrderDecisionRequestSchema>;

/**
 * NetSuite 可供量 read-through 單列（v2 擴充 — SR0.1）。availableQty 一律以
 * 「可供量」呈現且伴隨 syncedAt。qtyOnOrder/PSL/ROP 為 Tier-1 對帳「參考值」
 * —— 永不作為公式輸入（引擎的在途輸入為 Tier-2 衍生 qtyOpenToReceive，
 * Appendix D-1）；|差異| 逾容差會降低建議信心。前端先行定義，待後端確認。
 * 
 */
export const NetSuiteAvailabilityRowSchema = z.object({
  productId: z.string(),
  netSuiteItemId: z.string().nullable(),
  availableQty: z.number(),
  qtyOnOrder: z.number().nullable(),
  preferredStockLevel: z.number().optional().nullable(),
  reorderPoint: z.number().optional().nullable(),
  syncedAt: z.string().datetime()
});

export type NetSuiteAvailabilityRow = z.infer<typeof NetSuiteAvailabilityRowSchema>;

export const ShortageCheckLineSchema = z.object({
  soLineId: z.string(),
  productId: z.string(),
  sku: z.string(),
  productName: z.string(),
  qtyOrdered: z.number(),
  availableQty: z.number(),
  shortageQty: z.number()
});

export type ShortageCheckLine = z.infer<typeof ShortageCheckLineSchema>;

/**
 * 各地點可供量單列（Tier-1 read-through；每列帶 syncedAt）
 */
export const NetSuiteItemLocationRowSchema = z.object({
  location: z.string(),
  availableQty: z.number(),
  qtyOnOrder: z.number(),
  inTransit: z.number(),
  backOrdered: z.number(),
  syncedAt: z.string().datetime()
});

export type NetSuiteItemLocationRow = z.infer<typeof NetSuiteItemLocationRowSchema>;

/**
 * 各地點彙總列（Σ over rows；無 location，UI 提供粗體彙總標籤）
 */
export const NetSuiteItemLocationTotalsSchema = z.object({
  availableQty: z.number(),
  qtyOnOrder: z.number(),
  inTransit: z.number(),
  backOrdered: z.number(),
  syncedAt: z.string().datetime()
});

export type NetSuiteItemLocationTotals = z.infer<typeof NetSuiteItemLocationTotalsSchema>;

/**
 * 補貨建議 worklist 單列 = 衍生 ReorderSuggestion 引擎欄位（SR1.1；後端須逐欄
 * 重現）＋ 顯示增補。所有數量皆為 Tier-1 read-through（帶 syncedAt）或 Tier-2
 * 匯總，無一擁有現貨。derived-never-stored。前端先行定義，待後端確認。
 * 
 */
export const ReorderSuggestionRowSchema = z.object({
  productId: z.string(),
  scn: z.string().optional().nullable(),
  primaryVendorId: z.number().nullable(),
  inventoryPosition: z.number(),
  rop: z.number(),
  psl: z.number(),
  pooledDemandQty: z.number(),
  qtyOpenToReceive: z.number(),
  netsuiteQtyOnOrder: z.number().nullable(),
  netsuiteDelta: z.number().nullable(),
  suggestedQty: z.number(),
  rawQty: z.number(),
  moqClamped: z.boolean(),
  multipleRounded: z.boolean(),
  coverageDays: z.number().nullable(),
  severity: z.enum(['high', 'medium', 'low']),
  confidence: z.enum(['ok', 'low']),
  confidenceCauses: z.object({
  stale: z.boolean().optional(),
  variance: z.boolean().optional(),
  thinHistory: z.boolean().optional()
}),
  freshness: z.enum(['fresh', 'stale', 'expired']),
  syncedAt: z.string().datetime(),
  computedAt: z.string().datetime(),
  sku: z.string(),
  productName: z.string(),
  internalProductId: z.string(),
  categoryId: z.string(),
  vendorName: z.string().nullable(),
  moq: z.number().nullable(),
  duplicateOpenQty: z.number(),
  hasOpenPurchaseOrder: z.boolean()
});

export type ReorderSuggestionRow = z.infer<typeof ReorderSuggestionRowSchema>;

/**
 * 每個請求產品之 top-up 結果
 */
export const SendToPoolStatusSchema = z.enum(['pooled', 'skipped_no_vendor', 'not_suggested']);

export type SendToPoolStatus = z.infer<typeof SendToPoolStatusSchema>;

export const SendToPoolRequestSchema = z.object({
  productIds: z.array(z.string())
});

export type SendToPoolRequest = z.infer<typeof SendToPoolRequestSchema>;

/**
 * 商品搭配規則（#2166/#2167）— 既有 SKU 之間的母產品 × 搭配件規則。
 * 搭配件本身是一般產品（有自己的供應商列/最低訂購量/前置期，Figma 1:6）。
 * pairingType 即「是否詢問客戶」的指示（Figma 1:129）。pricingMode：
 * O1 未裁決 — null = 尚未選定（待林芊/Judy）；六個列舉值僅資料就緒、
 * 皆未接線（無預設值），計價接線不在本波（Q-K1/K2/K3）。
 * (parentProductId, componentProductId) 全域唯一；僅停用、不刪除。
 * Mock-first 契約 — 待後端確認。
 * 
 */
export const ProductPairingRuleSchema = z.object({
  id: z.string(),
  parentProductId: z.string(),
  componentProductId: z.string(),
  pairingType: z.enum(['mandatory', 'optional']),
  qtyRatio: PairingQtyRatioSchema,
  pricingMode: z.enum(['bundled_into_base', 'free', 'separate_line', 'fixed_amount', 'fixed_discount', 'pct_discount']).nullable(),
  isActive: z.boolean(),
  notes: z.string().optional().nullable(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime()
});

export type ProductPairingRule = z.infer<typeof ProductPairingRuleSchema>;

/**
 * 產品主檔（S1.2 — 欄位 Must 凍結於 xlsx col-J MoSCoW (R&D)，74 列，
 * 以 Field Label 對齊；create-required = 8 keys；Product Ready = create-8
 * + 3 描述 + basePrice + costingMethod，enrichment only）。主要識別 =
 * Internal Product ID（PRD-xxxxxx，系統指派、不可輸入）；
 * NetSuite Item ID 為唯讀外部對應；SCN 為選填、可回溯的共用產品連結。
 * 
 */
export const ProductSchema = z.object({
  id: z.string(),
  internalProductId: z.string(),
  scn: z.string().optional().nullable(),
  sku: z.string(),
  displayName: z.string(),
  subsidiaryId: z.string(),
  includeChildren: z.boolean().optional(),
  categoryId: z.string(),
  secondaryCategoryIds: z.array(z.string()).optional(),
  categoryPath: z.string().optional(),
  unitsTypeId: z.string(),
  stockUnit: z.string(),
  purchaseUnit: z.string(),
  saleUnit: z.string(),
  salesDescription: z.string().optional(),
  purchaseDescription: z.string().optional(),
  purchaseOrderNote: z.string().optional(),
  customsDescription: z.string().optional(),
  netSuiteItemId: z.string().optional().nullable(),
  netSuiteSyncedAt: z.string().datetime().optional().nullable(),
  oemNo: z.string().optional().nullable(),
  mpn: z.string().optional().nullable(),
  upcCode: z.string().optional().nullable(),
  customerPartNumbers: z.array(ProductCustomerPartNumberSchema).optional(),
  sparePartNumbers: z.array(ProductSparePartNumberSchema).optional(),
  vendorLines: z.array(ProductVendorLineSchema).optional(),
  images: z.array(ProductImageSlotSchema).optional(),
  fitment: ProductFitmentSchema.optional(),
  countryOfManufacture: z.string().optional(),
  lengthMm: z.number().optional(),
  widthMm: z.number().optional(),
  heightMm: z.number().optional(),
  weightKg: z.number().optional(),
  cubicFeet: z.number().optional(),
  pricingAnchor: z.enum(['purchase_cost', 'landed_cost', 'pricing_base']).optional().nullable(),
  costingMethod: z.enum(['FIFO', 'MOVING_AVERAGE', 'STANDARD', 'WEIGHTED_AVERAGE']).optional().nullable(),
  costingMethodLocked: z.boolean().optional(),
  basePrice: z.number().optional().nullable(),
  currency: z.string().optional(),
  minQty: z.number().optional(),
  maxQty: z.number().optional(),
  itemDefinedCost: z.number().optional(),
  accounting: ProductAccountingRefsSchema.optional(),
  planning: z.object({
  reorderPoint: z.number().optional().nullable(),
  reorderPointAuto: z.boolean().optional().nullable(),
  preferredStockLevel: z.number().optional().nullable(),
  preferredStockLevelDays: z.number().optional().nullable(),
  preferredStockLevelAuto: z.boolean().optional().nullable(),
  reorderMultiple: z.number().optional().nullable(),
  purchaseLeadTimeDays: z.number().optional().nullable(),
  purchaseLeadTimeAuto: z.boolean().optional().nullable(),
  safetyStockDays: z.number().optional().nullable(),
  seasonalDemand: z.boolean().optional().nullable(),
  expectedDemandChangePct: z.number().optional().nullable(),
  doNotOrder: z.boolean().optional().nullable(),
  preOrder: z.boolean().optional().nullable()
}).optional(),
  nameZh: z.string().optional(),
  isShared: z.boolean().optional(),
  variant: z.string().optional(),
  extractYear: z.number().optional(),
  manufacturer: z.string().optional(),
  faSubCategory: z.string().optional(),
  ageGroup: z.string().optional(),
  ageStartYear: z.number().optional(),
  ageEndYear: z.number().optional(),
  productNotes: z.array(ProductNoteSchema).optional(),
  damageStockNotes: z.string().optional(),
  itemComment: z.string().optional(),
  weightUnit: z.enum(['kg', 'g', 'lb']).optional(),
  minQtyUnit: z.string().optional(),
  maxQtyUnit: z.string().optional(),
  itemDefinedCostUnit: z.string().optional(),
  name: z.string().optional(),
  customerDescription: z.string().optional(),
  vendorDescription: z.string().optional(),
  oemPartNumber: z.string().optional().nullable(),
  manufacturerPartNumber: z.string().optional().nullable(),
  baseUom: z.string().optional(),
  purchaseUom: z.string().optional(),
  salesUom: z.string().optional(),
  purchaseToBaseFactor: z.number().optional(),
  salesToBaseFactor: z.number().optional(),
  lengthCm: z.number().optional(),
  widthCm: z.number().optional(),
  heightCm: z.number().optional(),
  grossWeightKg: z.number().optional(),
  cbm: z.number().optional(),
  preferredVendorId: z.number().optional().nullable(),
  preferredVendorName: z.string().optional().nullable(),
  listPrice: z.number().optional(),
  occStatus: z.enum(['ready', 'blocked', 'draft']).optional(),
  occBlockedFields: z.array(z.string()).optional(),
  isActive: z.boolean(),
  readiness: ProductReadinessSchema.optional(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime()
});

export type Product = z.infer<typeof ProductSchema>;

/**
 * 建立必填 = 識別＋單位＋分類（恰好）。單位未填時預設 PCS（R8）。
 * SCN 與 Internal Product ID 永不在建立時輸入；供應商列走
 * /products/{id}/vendors。
 * 
 */
export const CreateProductRequestSchema = z.object({
  sku: z.string(),
  displayName: z.string(),
  subsidiaryId: z.string(),
  categoryId: z.string(),
  includeChildren: z.boolean().optional(),
  unitsTypeId: z.string().optional(),
  stockUnit: z.string().optional(),
  purchaseUnit: z.string().optional(),
  saleUnit: z.string().optional(),
  secondaryCategoryIds: z.array(z.string()).optional(),
  salesDescription: z.string().optional(),
  purchaseDescription: z.string().optional(),
  purchaseOrderNote: z.string().optional(),
  customsDescription: z.string().optional(),
  productNotes: z.array(ProductNoteSchema).optional(),
  damageStockNotes: z.string().optional(),
  itemComment: z.string().optional(),
  variant: z.string().optional(),
  extractYear: z.number().optional(),
  manufacturer: z.string().optional(),
  faSubCategory: z.string().optional(),
  ageGroup: z.string().optional(),
  ageStartYear: z.number().optional(),
  ageEndYear: z.number().optional(),
  isShared: z.boolean().optional(),
  netSuiteItemId: z.string().optional().nullable(),
  oemNo: z.string().optional().nullable(),
  mpn: z.string().optional().nullable(),
  upcCode: z.string().optional().nullable(),
  customerPartNumbers: z.array(ProductCustomerPartNumberSchema).optional(),
  sparePartNumbers: z.array(ProductSparePartNumberSchema).optional(),
  images: z.array(ProductImageSlotSchema).optional(),
  fitment: ProductFitmentSchema.optional(),
  countryOfManufacture: z.string().optional(),
  lengthMm: z.number().optional(),
  widthMm: z.number().optional(),
  heightMm: z.number().optional(),
  weightKg: z.number().optional(),
  weightUnit: z.enum(['kg', 'g', 'lb']).optional(),
  costingMethod: z.enum(['FIFO', 'MOVING_AVERAGE', 'STANDARD', 'WEIGHTED_AVERAGE']).optional().nullable(),
  pricingAnchor: z.enum(['purchase_cost', 'landed_cost', 'pricing_base']).optional().nullable(),
  basePrice: z.number().optional().nullable(),
  currency: z.string().optional(),
  minQty: z.number().optional(),
  minQtyUnit: z.string().optional(),
  maxQty: z.number().optional(),
  maxQtyUnit: z.string().optional(),
  itemDefinedCost: z.number().optional(),
  itemDefinedCostUnit: z.string().optional(),
  accounting: ProductAccountingRefsSchema.optional(),
  planning: z.object({
  reorderPoint: z.number().optional().nullable(),
  reorderPointAuto: z.boolean().optional().nullable(),
  preferredStockLevel: z.number().optional().nullable(),
  preferredStockLevelDays: z.number().optional().nullable(),
  preferredStockLevelAuto: z.boolean().optional().nullable(),
  reorderMultiple: z.number().optional().nullable(),
  purchaseLeadTimeDays: z.number().optional().nullable(),
  purchaseLeadTimeAuto: z.boolean().optional().nullable(),
  safetyStockDays: z.number().optional().nullable(),
  seasonalDemand: z.boolean().optional().nullable(),
  expectedDemandChangePct: z.number().optional().nullable(),
  doNotOrder: z.boolean().optional().nullable(),
  preOrder: z.boolean().optional().nullable()
}).optional(),
  nameZh: z.string().optional()
});

export type CreateProductRequest = z.infer<typeof CreateProductRequestSchema>;

/**
 * 產品分類節點（5層樹狀結構；產品僅持有單一 categoryId 主鍵）
 */
export const ProductCategorySchema = z.object({
  id: z.string(),
  code: z.string(),
  parentId: z.string().nullable(),
  depth: z.number().min(1).max(5),
  path: z.string(),
  sortOrder: z.number(),
  nameEn: z.string(),
  nameZh: z.string(),
  isActive: z.boolean(),
  netsuiteSegmentValueId: z.string().optional(),
  description: z.string().optional(),
  iconKey: z.string().optional(),
  synonyms: z.array(z.string()).optional(),
  externalMappings: CategoryExternalMappingsSchema.optional()
});

export type ProductCategory = z.infer<typeof ProductCategorySchema>;

export const UpdateProductCategoryRequestSchema = z.object({
  nameEn: z.string().optional(),
  nameZh: z.string().optional(),
  sortOrder: z.number().optional(),
  netsuiteSegmentValueId: z.string().optional(),
  description: z.string().optional(),
  iconKey: z.string().optional(),
  synonyms: z.array(z.string()).optional(),
  externalMappings: CategoryExternalMappingsSchema.optional(),
  comment: z.string()
});

export type UpdateProductCategoryRequest = z.infer<typeof UpdateProductCategoryRequestSchema>;

/**
 * 主資料聯絡人列表項目
 */
export const MasterDataContactListItemSchema = z.object({
  contactId: z.number().optional(),
  name: z.string().optional(),
  jobTitle: z.string().optional(),
  companyName: z.string().optional(),
  phoneNumber: z.string().optional(),
  mobile: z.string().optional(),
  fax: z.string().optional(),
  email: z.string().optional(),
  address: z.string().optional(),
  isPrimary: z.boolean().optional(),
  isActive: z.boolean().optional(),
  vendorId: z.number().optional().nullable(),
  customerId: z.number().optional().nullable(),
  affiliations: z.array(ContactAffiliationSchema).optional().nullable(),
  createdAt: z.string().datetime().optional(),
  updatedAt: z.string().datetime().optional()
});

export type MasterDataContactListItem = z.infer<typeof MasterDataContactListItemSchema>;

export const CategorySchema = z.object({
  categoryId: z.number().optional(),
  category: z.string().optional(),
  types: z.array(CategoryTypeSchema).optional(),
  description: z.string().optional(),
  color: z.string().optional(),
  isActive: z.boolean().optional(),
  createdAt: z.string().datetime().optional(),
  updatedAt: z.string().datetime().optional()
});

export type Category = z.infer<typeof CategorySchema>;

export const VendorListItemSchema = z.object({
  vendorId: z.number(),
  vendorName: z.string(),
  individual: z.boolean(),
  categories: z.array(CategoryInfoSchema),
  abn: z.string(),
  address: z.string(),
  phoneNumber: z.string().optional(),
  isActive: z.boolean().optional(),
  internalContact: InternalContactSchema.nullable(),
  createdAt: z.string().datetime().optional(),
  updatedAt: z.string().datetime().optional()
});

export type VendorListItem = z.infer<typeof VendorListItemSchema>;

export const VendorDetailSchema = z.object({
  vendorId: z.number().optional(),
  name: z.string().optional(),
  alternativeName: z.string().optional(),
  nickname: z.string().optional(),
  vendorCode: z.string().optional(),
  individual: z.boolean().optional(),
  categories: z.array(CategoryInfoSchema).optional(),
  primaryPort: z.string().optional(),
  internalContact: InternalContactSchema.optional(),
  email: z.string().email().optional(),
  fax: z.string().optional(),
  phoneNumber: z.string().optional(),
  web: z.string().url().optional(),
  notes: z.string().optional(),
  countryId: z.number().optional(),
  stateId: z.number().optional(),
  cityId: z.number().optional(),
  postcode: z.string().optional(),
  address: z.string().optional(),
  addressLang: z.string().max(3).optional(),
  parseStatus: z.enum(['parsed', 'pending', 'failed']).optional(),
  isActive: z.boolean().optional(),
  createdAt: z.string().datetime().optional(),
  updatedAt: z.string().datetime().optional()
});

export type VendorDetail = z.infer<typeof VendorDetailSchema>;

export const VendorContactSchema = z.object({
  contactId: z.number().optional(),
  isPrimary: z.boolean().optional(),
  name: z.string().optional(),
  jobTitle: z.string().optional(),
  companyName: z.string().optional(),
  phoneCountryCode: z.string().optional(),
  phoneNumber: z.string().optional(),
  phoneExtension: z.string().optional(),
  mobileCountryCode: z.string().optional(),
  mobileNumber: z.string().optional(),
  fax: z.string().optional(),
  email: z.string().email().optional(),
  address: z.string().optional(),
  addressLang: z.string().optional(),
  parseStatus: z.enum(['parsed', 'pending', 'failed']).optional(),
  isActive: z.boolean().optional(),
  affiliations: z.array(ContactAffiliationSchema).optional().nullable(),
  createdAt: z.string().datetime().optional(),
  updatedAt: z.string().datetime().optional()
});

export type VendorContact = z.infer<typeof VendorContactSchema>;

export const VendorContactCreateSchema = z.object({
  vendorId: z.number(),
  isPrimary: z.boolean().optional(),
  name: z.string().max(255),
  jobTitle: z.string().max(255),
  phoneCountryCode: z.string().max(10),
  phoneNumber: z.string().max(50),
  phoneExtension: z.string().max(20).optional(),
  mobileCountryCode: z.string().max(10).optional(),
  mobileNumber: z.string().max(50).optional(),
  fax: z.string().max(50).optional(),
  email: z.string().email(),
  address: z.string().optional(),
  addressLang: z.string().max(3).optional(),
  isActive: z.boolean().optional(),
  affiliations: z.array(ContactAffiliationInputSchema).optional()
});

export type VendorContactCreate = z.infer<typeof VendorContactCreateSchema>;

export const VendorContactUpdateSchema = z.object({
  isPrimary: z.boolean().optional(),
  name: z.string().max(255),
  jobTitle: z.string().max(255),
  phoneCountryCode: z.string().max(10),
  phoneNumber: z.string().max(50),
  phoneExtension: z.string().max(20).optional(),
  mobileCountryCode: z.string().max(10).optional(),
  mobileNumber: z.string().max(50).optional(),
  fax: z.string().max(50).optional(),
  email: z.string().email(),
  address: z.string().optional(),
  addressLang: z.string().max(3).optional(),
  isActive: z.boolean().optional(),
  affiliations: z.array(ContactAffiliationInputSchema).optional()
});

export type VendorContactUpdate = z.infer<typeof VendorContactUpdateSchema>;

export const AuditLogResponseSchema = z.object({
  data: z.array(AuditLogSchema).optional(),
  total: z.number().optional(),
  limit: z.number().optional(),
  offset: z.number().optional()
});

export type AuditLogResponse = z.infer<typeof AuditLogResponseSchema>;

export const LocationHierarchySchema = z.object({
  countryId: z.number().optional(),
  countryCode: z.string().optional(),
  countryName: z.string().optional(),
  states: z.array(z.object({
  stateId: z.number().optional(),
  stateCode: z.string().optional(),
  stateName: z.string().optional(),
  cities: z.array(CitySchema).optional()
})).optional()
});

export type LocationHierarchy = z.infer<typeof LocationHierarchySchema>;

/**
 * 客戶列表項目
 */
export const CustomerListItemSchema = z.object({
  customerId: z.number(),
  customerName: z.string(),
  individual: z.boolean(),
  categories: z.array(z.string()),
  localTaxId: z.string(),
  address: z.string().optional().nullable(),
  phoneNumber: z.string().optional().nullable(),
  internalContact: InternalContactSchema.nullable(),
  isActive: z.boolean(),
  createdAt: z.string().datetime().optional(),
  updatedAt: z.string().datetime().optional()
});

export type CustomerListItem = z.infer<typeof CustomerListItemSchema>;

/**
 * 客戶完整資訊
 */
export const CustomerDetailSchema = z.object({
  customerId: z.number().optional(),
  name: z.string().optional(),
  alternativeName: z.string().optional().nullable(),
  nickname: z.string().optional().nullable(),
  individual: z.boolean().optional(),
  customerCode: z.string().optional(),
  localTaxId: z.string().optional(),
  categories: z.array(z.object({
  categoryId: z.number().optional(),
  category: z.string().optional()
})).optional(),
  internalContact: InternalContactSchema.optional().nullable(),
  parentCompany: z.object({
  companyId: z.number().optional(),
  companyName: z.string().optional()
}).optional().nullable(),
  defaultLocation: z.object({
  locationId: z.number().optional(),
  locationName: z.string().optional()
}).optional().nullable(),
  subsidiaries: z.array(z.object({
  companyId: z.number().optional(),
  companyName: z.string().optional()
})).optional(),
  relatedOrganisations: z.array(z.object({
  companyId: z.number().optional(),
  companyName: z.string().optional(),
  relationType: z.string().optional()
})).optional(),
  isActiveOnlineAccount: z.boolean().optional(),
  email: z.string().email().optional().nullable(),
  fax: z.string().optional().nullable(),
  phoneCountryCode: z.string().optional().nullable(),
  phoneNumber: z.string().optional().nullable(),
  phoneExtension: z.string().optional().nullable(),
  web: z.string().url().optional().nullable(),
  accountNotes: z.string().optional().nullable(),
  deliveryNotes: z.string().optional().nullable(),
  salesNotes: z.string().optional().nullable(),
  countryId: z.number().optional().nullable(),
  stateId: z.number().optional().nullable(),
  cityId: z.number().optional().nullable(),
  postcode: z.string().optional().nullable(),
  address: z.string().optional().nullable(),
  addressLang: z.string().optional().nullable(),
  parseStatus: z.enum(['parsed', 'pending', 'failed']).optional(),
  isActive: z.boolean().optional(),
  createdAt: z.string().datetime().optional(),
  updatedAt: z.string().datetime().optional()
});

export type CustomerDetail = z.infer<typeof CustomerDetailSchema>;

/**
 * 定價引擎完整結果。持久化在銷售訂單行上之後即為不可變快照 —
 * 永不靜默改價；重新定價 = 明確更新行時建立全新快照。
 * 
 */
export const PriceResolutionSchema = z.object({
  unitPrice: z.number().nullable(),
  currency: z.string().nullable(),
  pricingAnchor: z.enum(['purchase_cost', 'landed_cost', 'pricing_base']),
  winner: z.enum(['special_override', 'customer_level', 'list_price', 'unresolved']),
  trace: z.array(PriceTraceEntrySchema),
  waterfall: z.array(PriceWaterfallRowSchema),
  marginVsLanded: z.object({
  amount: z.number(),
  pct: z.number()
}).nullable(),
  asOf: z.string()
});

export type PriceResolution = z.infer<typeof PriceResolutionSchema>;

/**
 * 銷售訂單行 — 產品就緒管制；priceTrace 為持久化不可變快照
 */
export const SalesOrderLineSchema = z.object({
  id: z.string(),
  productId: z.string(),
  sku: z.string(),
  productName: z.string(),
  customerPartNumber: z.string().optional().nullable(),
  qty: z.number(),
  unitPrice: z.number(),
  currency: z.string(),
  resolvedUnitPrice: z.number().nullable(),
  priceTrace: z.unknown().nullable(),
  overrideReason: z.string().optional().nullable(),
  discountPct: z.number().optional(),
  lineTotal: z.number(),
  landedCost: z.number().nullable(),
  estMargin: z.number().nullable(),
  estMarginPct: z.number().nullable()
});

export type SalesOrderLine = z.infer<typeof SalesOrderLineSchema>;

/**
 * 銷售訂單（S2.1）— externalOrderNumber 為匯入去重鍵
 */
export const SalesOrderSchema = z.object({
  id: z.string(),
  soNumber: z.string(),
  customerId: z.number(),
  customerName: z.string(),
  status: z.enum(['draft', 'confirmed', 'converted_to_po', 'shipped', 'invoiced', 'paid']),
  source: z.enum(['manual', 'netsuite', 'excel']),
  externalOrderNumber: z.string().nullable(),
  orderDate: z.string(),
  expectedShipDate: z.string().nullable(),
  deliveryDate: z.string().nullable(),
  currency: z.string(),
  priceStatus: z.enum(['ok', 'pending_approval']),
  priceHoldReasons: z.array(z.enum(['deviation', 'low_margin'])),
  marginFloorPct: z.number().nullable(),
  priceApproval: z.object({
  approvedBy: z.string(),
  reason: z.string(),
  approvedAt: z.string().datetime()
}).nullable(),
  priceApprovalHistory: z.array(z.object({
  approvedBy: z.string(),
  reason: z.string(),
  approvedAt: z.string().datetime(),
  holdReasons: z.array(z.enum(['deviation', 'low_margin'])),
  marginFloorPct: z.number().nullable(),
  voidedAt: z.string().datetime()
})).optional(),
  lines: z.array(SalesOrderLineSchema),
  totalAmount: z.number(),
  totalEstMargin: z.number().nullable(),
  linkedPoIds: z.array(z.string()).optional(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime()
});

export type SalesOrder = z.infer<typeof SalesOrderSchema>;

export const ConfirmChecklistResultSchema = z.object({
  ok: z.boolean(),
  items: z.array(ConfirmChecklistItemSchema)
});

export type ConfirmChecklistResult = z.infer<typeof ConfirmChecklistResultSchema>;

export const CreateSalesOrderRequestSchema = z.object({
  customerId: z.number(),
  currency: z.string().optional(),
  externalOrderNumber: z.string().optional(),
  expectedShipDate: z.string().optional(),
  deliveryDate: z.string().optional(),
  lines: z.array(CreateSalesOrderLineInputSchema)
});

export type CreateSalesOrderRequest = z.infer<typeof CreateSalesOrderRequestSchema>;

/**
 * lines 為全量替換；替換行取得全新價格快照
 */
export const UpdateSalesOrderRequestSchema = z.object({
  expectedShipDate: z.string().optional(),
  deliveryDate: z.string().optional(),
  currency: z.string().optional(),
  lines: z.array(CreateSalesOrderLineInputSchema).optional()
});

export type UpdateSalesOrderRequest = z.infer<typeof UpdateSalesOrderRequestSchema>;

export const ImportSalesOrdersResponseSchema = z.object({
  data: z.array(SalesOrderSchema),
  issues: z.array(SalesOrderImportIssueSchema),
  exceptions: z.array(SalesOrderImportExceptionSchema),
  summary: z.object({
  created: z.number(),
  updated: z.number(),
  failed: z.number()
})
});

export type ImportSalesOrdersResponse = z.infer<typeof ImportSalesOrdersResponseSchema>;

/**
 * 檢查第二次輔助 — 開放 PO 數 / 近三次最低價提示 / 重複下單警告
 */
export const PoLineSecondCheckSchema = z.object({
  openPoCount: z.number(),
  recentPrices: z.array(PoSecondCheckPriceRowSchema),
  lowestRecentPrice: z.number().nullable(),
  duplicateOpenQty: z.number(),
  warnings: z.array(z.enum(['above_lowest_recent', 'duplicate_open_order']))
});

export type PoLineSecondCheck = z.infer<typeof PoLineSecondCheckSchema>;

/**
 * 採購訂單行 — purchasePrice 由供應商行議價 unitCost 自動帶入
 */
export const PurchaseOrderLineSchema = z.object({
  id: z.string(),
  productId: z.string(),
  sku: z.string(),
  productName: z.string(),
  qty: z.number(),
  purchasePrice: z.number(),
  unitCost: z.number(),
  currency: z.string(),
  priceSource: z.enum(['vendor_line', 'manual']),
  expectedDate: z.string().nullable(),
  secondCheck: z.unknown().nullable(),
  allocations: z.array(AllocationSchema).optional()
});

export type PurchaseOrderLine = z.infer<typeof PurchaseOrderLineSchema>;

/**
 * 採購訂單（S3.1）— vendorId 必填（US-266）；poNumber = PO- + 序號
 * （規則可設定，待 Joe 定案）；核決為獨立狀態（>NT$200k 僅擋送出）。
 * 
 */
export const PurchaseOrderSchema = z.object({
  id: z.string(),
  poNumber: z.string(),
  sourceSoId: z.string().nullable(),
  sourceSoNumber: z.string().optional().nullable(),
  vendorId: z.number(),
  vendorName: z.string(),
  vendorPrefillSource: z.enum(['preferred', 'manual', 'so']).nullable(),
  status: z.enum(['draft', 'sent', 'vendor_confirmed', 'shipped', 'received']),
  orderDate: z.string(),
  currency: z.string(),
  lines: z.array(PurchaseOrderLineSchema),
  totalAmount: z.number(),
  approvalRequired: z.boolean(),
  approvalStatus: z.enum(['not_required', 'pending', 'approved', 'rejected']),
  approval: z.object({
  decision: z.enum(['approve', 'reject']),
  decidedBy: z.string(),
  reason: z.string(),
  decidedAt: z.string().datetime()
}).nullable(),
  vendorAcknowledgedAt: z.string().datetime().nullable(),
  vendorConfirmationNote: z.string().nullable(),
  paymentTerms: z.string().nullable(),
  expectedDeliveryDate: z.string().nullable(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime()
});

export type PurchaseOrder = z.infer<typeof PurchaseOrderSchema>;

export const CreatePurchaseOrderLineInputSchema = z.object({
  productId: z.string(),
  qty: z.number().min(1),
  purchasePrice: z.number().optional(),
  unitCost: z.number().optional(),
  expectedDate: z.string().optional(),
  soLineId: z.string().optional(),
  allocations: z.array(CreatePurchaseOrderAllocationInputSchema).optional()
});

export type CreatePurchaseOrderLineInput = z.infer<typeof CreatePurchaseOrderLineInputSchema>;

export const CreatePurchaseOrderRequestSchema = z.object({
  vendorId: z.number(),
  currency: z.string().optional(),
  sourceSoId: z.string().optional(),
  paymentTerms: z.string().optional(),
  expectedDeliveryDate: z.string().optional(),
  lines: z.array(CreatePurchaseOrderLineInputSchema)
});

export type CreatePurchaseOrderRequest = z.infer<typeof CreatePurchaseOrderRequestSchema>;

export const NetSuiteAvailabilityResponseSchema = z.object({
  data: z.array(NetSuiteAvailabilityRowSchema),
  syncedAt: z.string().datetime(),
  lastAvailabilityChange: z.string().datetime().optional()
});

export type NetSuiteAvailabilityResponse = z.infer<typeof NetSuiteAvailabilityResponseSchema>;

export const ShortageCheckResponseSchema = z.object({
  soId: z.string(),
  syncedAt: z.string().datetime(),
  hasShortage: z.boolean(),
  lines: z.array(ShortageCheckLineSchema)
});

export type ShortageCheckResponse = z.infer<typeof ShortageCheckResponseSchema>;

/**
 * 單一產品之各地點可供量快照。totals 由伺服器彙總；totals.availableQty ===
 * 標頭 availableQty。前端先行定義，待後端確認。
 * 
 */
export const NetSuiteItemLocationsResponseSchema = z.object({
  productId: z.string(),
  netSuiteItemId: z.string().nullable(),
  rows: z.array(NetSuiteItemLocationRowSchema),
  totals: NetSuiteItemLocationTotalsSchema,
  syncedAt: z.string().datetime(),
  lastAvailabilityChange: z.string().datetime().optional()
});

export type NetSuiteItemLocationsResponse = z.infer<typeof NetSuiteItemLocationsResponseSchema>;

/**
 * 補貨建議清單（無分頁）；envelope 與每列皆帶 syncedAt。前端先行定義，待後端確認。
 */
export const ReorderSuggestionListResponseSchema = z.object({
  data: z.array(ReorderSuggestionRowSchema),
  syncedAt: z.string().datetime(),
  computedAt: z.string().datetime()
});

export type ReorderSuggestionListResponse = z.infer<typeof ReorderSuggestionListResponseSchema>;

/**
 * 單一產品 top-up 結果（Appendix D-4）。totalPooled === suggestedQty（不變式）：
 * SO 需求 + 補貨來源 之池總和恰等於 suggestedQty，SO 需求永不重複覆蓋。
 * 
 */
export const SendToPoolResultItemSchema = z.object({
  productId: z.string(),
  vendorId: z.number().nullable(),
  status: SendToPoolStatusSchema,
  suggestedQty: z.number(),
  pooledDemandQty: z.number(),
  topUpQty: z.number(),
  reorderPooledQty: z.number(),
  totalPooled: z.number(),
  duplicateOpenQty: z.number(),
  hasOpenPurchaseOrder: z.boolean()
});

export type SendToPoolResultItem = z.infer<typeof SendToPoolResultItemSchema>;

export const SendToPoolResponseSchema = z.object({
  data: z.array(SendToPoolResultItemSchema),
  syncedAt: z.string().datetime(),
  computedAt: z.string().datetime()
});

export type SendToPoolResponse = z.infer<typeof SendToPoolResponseSchema>;


// Convenience object for accessing all schemas
export const schemas = {
  MvpRecord: MvpRecordSchema,
  ProductReadiness: ProductReadinessSchema,
  ProductCustomerPartNumber: ProductCustomerPartNumberSchema,
  ProductSparePartNumber: ProductSparePartNumberSchema,
  PairingQtyRatio: PairingQtyRatioSchema,
  ProductVendorLine: ProductVendorLineSchema,
  ProductFitment: ProductFitmentSchema,
  ProductImageSlot: ProductImageSlotSchema,
  ProductAccountingRefs: ProductAccountingRefsSchema,
  ProductNote: ProductNoteSchema,
  UpdateProductRequest: UpdateProductRequestSchema,
  CheckSkuResponse: CheckSkuResponseSchema,
  ProductSalesStats: ProductSalesStatsSchema,
  ProductVendorPrefill: ProductVendorPrefillSchema,
  CategoryExternalMappings: CategoryExternalMappingsSchema,
  CategoryLevelConfig: CategoryLevelConfigSchema,
  CreateProductCategoryRequest: CreateProductCategoryRequestSchema,
  ReparentProductCategoryRequest: ReparentProductCategoryRequestSchema,
  DeactivateProductCategoryRequest: DeactivateProductCategoryRequestSchema,
  UpdateCategoryLevelRequest: UpdateCategoryLevelRequestSchema,
  CategoryAuditRow: CategoryAuditRowSchema,
  Error: ErrorSchema,
  Pagination: PaginationSchema,
  ContactAffiliation: ContactAffiliationSchema,
  ContactAffiliationInput: ContactAffiliationInputSchema,
  UserProfile: UserProfileSchema,
  CategoryType: CategoryTypeSchema,
  CategoryInfo: CategoryInfoSchema,
  CategoryCreate: CategoryCreateSchema,
  CategoryUpdate: CategoryUpdateSchema,
  Company: CompanySchema,
  InternalContact: InternalContactSchema,
  VendorCreate: VendorCreateSchema,
  VendorUpdate: VendorUpdateSchema,
  VendorPatch: VendorPatchSchema,
  PaymentTerm: PaymentTermSchema,
  TaxType: TaxTypeSchema,
  VendorPayment: VendorPaymentSchema,
  VendorPaymentCreate: VendorPaymentCreateSchema,
  VendorPaymentUpdate: VendorPaymentUpdateSchema,
  VendorBank: VendorBankSchema,
  VendorBankCreate: VendorBankCreateSchema,
  VendorBankUpdate: VendorBankUpdateSchema,
  BankInfo: BankInfoSchema,
  BankBranchInfo: BankBranchInfoSchema,
  VendorTransaction: VendorTransactionSchema,
  VendorLocation: VendorLocationSchema,
  VendorLocationCreate: VendorLocationCreateSchema,
  VendorLocationUpdate: VendorLocationUpdateSchema,
  AuditLog: AuditLogSchema,
  Continent: ContinentSchema,
  Country: CountrySchema,
  State: StateSchema,
  City: CitySchema,
  LocationByPostcode: LocationByPostcodeSchema,
  CompanyInfo: CompanyInfoSchema,
  CustomerCreate: CustomerCreateSchema,
  CustomerUpdate: CustomerUpdateSchema,
  CustomerBank: CustomerBankSchema,
  CustomerBankCreate: CustomerBankCreateSchema,
  CustomerBankUpdate: CustomerBankUpdateSchema,
  CustomerLocation: CustomerLocationSchema,
  CustomerLocationCreate: CustomerLocationCreateSchema,
  CustomerLocationUpdate: CustomerLocationUpdateSchema,
  CustomerPayment: CustomerPaymentSchema,
  CustomerPaymentCreate: CustomerPaymentCreateSchema,
  CustomerPaymentUpdate: CustomerPaymentUpdateSchema,
  PriceTraceEntry: PriceTraceEntrySchema,
  PriceWaterfallRow: PriceWaterfallRowSchema,
  ConfirmChecklistItem: ConfirmChecklistItemSchema,
  CreateSalesOrderLineInput: CreateSalesOrderLineInputSchema,
  SalesOrderImportIssue: SalesOrderImportIssueSchema,
  SalesOrderImportException: SalesOrderImportExceptionSchema,
  Allocation: AllocationSchema,
  PoSecondCheckPriceRow: PoSecondCheckPriceRowSchema,
  CreatePurchaseOrderAllocationInput: CreatePurchaseOrderAllocationInputSchema,
  PurchaseOrderDecisionRequest: PurchaseOrderDecisionRequestSchema,
  NetSuiteAvailabilityRow: NetSuiteAvailabilityRowSchema,
  ShortageCheckLine: ShortageCheckLineSchema,
  NetSuiteItemLocationRow: NetSuiteItemLocationRowSchema,
  NetSuiteItemLocationTotals: NetSuiteItemLocationTotalsSchema,
  ReorderSuggestionRow: ReorderSuggestionRowSchema,
  SendToPoolStatus: SendToPoolStatusSchema,
  SendToPoolRequest: SendToPoolRequestSchema,
  ProductPairingRule: ProductPairingRuleSchema,
  Product: ProductSchema,
  CreateProductRequest: CreateProductRequestSchema,
  ProductCategory: ProductCategorySchema,
  UpdateProductCategoryRequest: UpdateProductCategoryRequestSchema,
  MasterDataContactListItem: MasterDataContactListItemSchema,
  Category: CategorySchema,
  VendorListItem: VendorListItemSchema,
  VendorDetail: VendorDetailSchema,
  VendorContact: VendorContactSchema,
  VendorContactCreate: VendorContactCreateSchema,
  VendorContactUpdate: VendorContactUpdateSchema,
  AuditLogResponse: AuditLogResponseSchema,
  LocationHierarchy: LocationHierarchySchema,
  CustomerListItem: CustomerListItemSchema,
  CustomerDetail: CustomerDetailSchema,
  PriceResolution: PriceResolutionSchema,
  SalesOrderLine: SalesOrderLineSchema,
  SalesOrder: SalesOrderSchema,
  ConfirmChecklistResult: ConfirmChecklistResultSchema,
  CreateSalesOrderRequest: CreateSalesOrderRequestSchema,
  UpdateSalesOrderRequest: UpdateSalesOrderRequestSchema,
  ImportSalesOrdersResponse: ImportSalesOrdersResponseSchema,
  PoLineSecondCheck: PoLineSecondCheckSchema,
  PurchaseOrderLine: PurchaseOrderLineSchema,
  PurchaseOrder: PurchaseOrderSchema,
  CreatePurchaseOrderLineInput: CreatePurchaseOrderLineInputSchema,
  CreatePurchaseOrderRequest: CreatePurchaseOrderRequestSchema,
  NetSuiteAvailabilityResponse: NetSuiteAvailabilityResponseSchema,
  ShortageCheckResponse: ShortageCheckResponseSchema,
  NetSuiteItemLocationsResponse: NetSuiteItemLocationsResponseSchema,
  ReorderSuggestionListResponse: ReorderSuggestionListResponseSchema,
  SendToPoolResultItem: SendToPoolResultItemSchema,
  SendToPoolResponse: SendToPoolResponseSchema,
};
